import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Check, X, FileCheck2 } from "lucide-react";

function StatusBadge({ status }) {
  const map = { pending: "bg-[#FEF3C7] text-[#92681A]", approved: "bg-[#E3F3ED] text-[#1A365D]", rejected: "bg-[#FBE6E6] text-[#C0392B]" };
  return <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium capitalize ${map[status] || map.pending}`}>{status}</span>;
}

export function ClaimsManager() {
  const [claims, setClaims] = useState([]);

  const load = async () => { try { const { data } = await api.get("/claims"); setClaims(data); } catch {} };
  useEffect(() => { load(); }, []);

  const review = async (id, action) => {
    try { await api.post(`/claims/${id}/${action}`); toast.success(`Claim ${action}d`); load(); }
    catch { toast.error("Could not update claim"); }
  };

  return (
    <div>
      <p className="text-[#4A534E] mb-6">Review and verify directors requesting to manage a facility. Approving grants them access and saves their inquiry email.</p>
      {claims.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-[#E8E6DF] text-[#5C6660]" data-testid="claims-empty">No claim requests yet.</div>
      ) : (
        <div className="space-y-3" data-testid="admin-claims">
          {claims.map((c) => (
            <div key={c.claim_id} className="bg-white border border-[#E8E6DF] rounded-2xl p-5">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-[#E3F3ED] flex items-center justify-center shrink-0"><ShieldCheck className="h-5 w-5 text-[#0077B6]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className="font-medium text-[#1C211F]">{c.full_name} <span className="text-[#5C6660] font-normal">· {c.title || "Director"}{c.relationship ? ` · ${c.relationship}` : ""}</span></span>
                    <StatusBadge status={c.status} />
                  </div>
                  <div className="text-sm text-[#4A534E]">Claiming: <b>{c.facility_name}</b></div>
                  <div className="text-xs text-[#5C6660] mt-0.5">{c.email} · {c.phone || "no phone"}</div>
                  {c.proof && <div className="mt-2"><span className="text-xs font-semibold text-[#1A365D]">Proof of authority:</span><p className="text-sm text-[#2A312D] bg-[#F7F6F2] rounded-xl p-3 mt-1">{c.proof}</p></div>}
                  {c.proof_document && <a href={c.proof_document} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-[#0077B6] mt-2 hover:underline"><FileCheck2 className="h-4 w-4" /> View supporting document</a>}
                  {c.declaration && <p className="text-xs text-[#5C6660] mt-2">✓ Legal declaration accepted {c.declaration_at ? `on ${new Date(c.declaration_at).toLocaleDateString()}` : ""}</p>}
                  {c.status === "pending" && (
                    <div className="flex gap-2 mt-3">
                      <Button onClick={() => review(c.claim_id, "approve")} className="bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl" data-testid="approve-claim-btn"><Check className="h-4 w-4 mr-1.5" /> Approve & verify</Button>
                      <Button onClick={() => review(c.claim_id, "reject")} variant="outline" className="rounded-xl border-[#E8E6DF] text-[#C0392B]" data-testid="reject-claim-btn"><X className="h-4 w-4 mr-1.5" /> Reject</Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
